import { useState, useRef, useEffect } from 'react';
import { useLabsFeatures } from '@/hooks/useLabsFeatures';
import type { User } from '@/services/api';

// Convert storage format @[Name](id) → display format @Name (shown in textarea)
function toDisplayFormat(text: string): string {
  return text.replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1');
}

// Extract existing mentions from storage format (used when editing a saved comment)
function extractMentions(text: string): Array<{ name: string; id: string }> {
  const regex = /@\[([^\]]+)\]\(([^)]+)\)/g;
  const found: Array<{ name: string; id: string }> = [];
  let match;
  while ((match = regex.exec(text)) !== null) {
    found.push({ name: match[1], id: match[2] });
  }
  return found;
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

interface CommentInputProps {
  onSubmit: (content: string) => Promise<void>;
  placeholder?: string;
  buttonText?: string;
  onCancel?: () => void;
  autoFocus?: boolean;
  initialValue?: string;
  users?: User[];  // for @mention autocomplete
}

export function CommentInput({
  onSubmit,
  placeholder = 'Write a comment...',
  buttonText = 'Comment',
  onCancel,
  autoFocus = false,
  initialValue = '',
  users = [],
}: CommentInputProps) {
  const labs = useLabsFeatures();
  const mentionsEnabled = labs.isEnabled('mentions');

  // Display friendly @Name format in textarea; storage @[Name](id) only on submit
  const [content, setContent] = useState(() => toDisplayFormat(initialValue));
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track inserted mentions so we can convert back to storage format on submit
  const [mentions, setMentions] = useState<Array<{ name: string; id: string }>>(
    () => extractMentions(initialValue)
  );

  // Mention autocomplete state
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownUsers, setDropdownUsers] = useState<User[]>([]);
  const [mentionAnchor, setMentionAnchor] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    if (!showDropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDropdown]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    const pos = e.target.selectionStart ?? val.length;

    if (mentionsEnabled && users.length > 0) {
      const textBeforeCursor = val.slice(0, pos);
      const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

      if (mentionMatch) {
        const search = mentionMatch[1].toLowerCase();
        setMentionAnchor(pos - mentionMatch[0].length);
        const filtered = users
          .filter((u) => u.isActive && u.name.toLowerCase().includes(search))
          .slice(0, 6);
        setDropdownUsers(filtered);
        setShowDropdown(filtered.length > 0);
        setSelectedIndex(0);
      } else {
        setShowDropdown(false);
      }
    }

    setContent(val);
  };

  const selectUser = (user: User) => {
    const cursorPos = textareaRef.current?.selectionStart ?? content.length;
    const before = content.slice(0, mentionAnchor);
    const after = content.slice(cursorPos);
    // Insert friendly display format — @Name space — in the textarea
    const displayText = `@${user.name} `;
    setContent(`${before}${displayText}${after}`);
    // Remember this mention so toStorageFormat can convert it back on submit
    setMentions((prev) => [...prev, { name: user.name, id: user.id }]);
    setShowDropdown(false);

    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = mentionAnchor + displayText.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showDropdown) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, dropdownUsers.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (dropdownUsers[selectedIndex]) selectUser(dropdownUsers[selectedIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setShowDropdown(false);
        return;
      }
    }

    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape' && onCancel) {
      onCancel();
    }
  };

  // Convert display @Name back to storage @[Name](id) before submitting
  const toStorageFormat = (displayContent: string): string => {
    let result = displayContent;
    // Replace longest names first to avoid partial matches (e.g. "John" inside "John Smith")
    const sorted = [...mentions].sort((a, b) => b.name.length - a.name.length);
    for (const { name, id } of sorted) {
      result = result.replace(
        new RegExp(`@${escapeRegex(name)}(?=[\\s,.]|$)`, 'g'),
        `@[${name}](${id})`
      );
    }
    return result;
  };

  const handleSubmit = async () => {
    if (!content.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmit(toStorageFormat(content.trim()));
      setContent('');
      setMentions([]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          rows={3}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400"
        />

        {/* @mention dropdown */}
        {mentionsEnabled && showDropdown && dropdownUsers.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute left-0 top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden"
          >
            {dropdownUsers.map((user, idx) => (
              <button
                key={user.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault(); // prevent textarea blur before selection
                  selectUser(user);
                }}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                  idx === selectedIndex
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs flex items-center justify-center font-medium flex-shrink-0">
                  {user.name.charAt(0).toUpperCase()}
                </span>
                <span className="font-medium">{user.name}</span>
                {user.title && (
                  <span className="text-xs text-gray-400 truncate">{user.title}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-2">
        <p className="text-xs text-gray-400">
          Ctrl+Enter to submit{mentionsEnabled && users.length > 0 ? ' · Type @ to mention' : ''}
        </p>
        <div className="flex items-center gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting}
            className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Posting...' : buttonText}
          </button>
        </div>
      </div>
    </div>
  );
}
